'use client';
import React, { useState } from 'react';
import { Plus, Star, TrendingUp, TrendingDown } from 'lucide-react';
import { useMarketData } from '@/contexts/MarketDataContext';

const WATCHLIST_SYMBOLS = [
  { id: 'wl-001', symbol: 'RELIANCE',   name: 'Reliance Industries' },
  { id: 'wl-002', symbol: 'TCS',        name: 'Tata Consultancy Services' },
  { id: 'wl-003', symbol: 'HDFCBANK',   name: 'HDFC Bank Ltd' },
  { id: 'wl-004', symbol: 'INFY',       name: 'Infosys Ltd' },
  { id: 'wl-005', symbol: 'ICICIBANK',  name: 'ICICI Bank Ltd' },
  { id: 'wl-006', symbol: 'BAJFINANCE', name: 'Bajaj Finance Ltd' },
  { id: 'wl-007', symbol: 'WIPRO',      name: 'Wipro Ltd' },
  { id: 'wl-008', symbol: 'AXISBANK',   name: 'Axis Bank Ltd' },
  { id: 'wl-009', symbol: 'SUNPHARMA',  name: 'Sun Pharmaceutical' },
  { id: 'wl-010', symbol: 'MARUTI',     name: 'Maruti Suzuki India' },
  { id: 'wl-011', symbol: 'TATASTEEL',  name: 'Tata Steel Ltd' },
  { id: 'wl-012', symbol: 'ONGC',       name: 'Oil & Natural Gas Corp' },
];

export default function WatchlistPanel() {
  const [hovered, setHovered] = useState<string | null>(null);
  const { ticks } = useMarketData();

  return (
    <div className="card-elevated overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Star size={14} className="text-warning" />
          <h2 className="text-sm font-semibold text-foreground">Watchlist</h2>
          <span className="text-2xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
            {WATCHLIST_SYMBOLS?.length} stocks
          </span>
        </div>
        <button className="flex items-center gap-1 text-2xs font-semibold text-primary hover:bg-primary/10 px-2 py-1 rounded-md transition-all">
          <Plus size={12} />Add Stock
        </button>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs min-w-[360px]">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-3 md:px-4 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide">Symbol</th>
              <th className="text-right px-2 md:px-3 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide">LTP</th>
              <th className="text-right px-2 md:px-3 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide">Change</th>
              <th className="text-right px-2 md:px-3 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide hidden lg:table-cell">High</th>
              <th className="text-right px-2 md:px-3 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide hidden lg:table-cell">Low</th>
              <th className="text-right px-2 md:px-3 py-2 text-2xs font-semibold text-muted-foreground uppercase tracking-wide hidden xl:table-cell">Volume</th>
              <th className="px-2 md:px-3 py-2 w-16 hidden sm:table-cell" />
            </tr>
          </thead>
          <tbody>
            {WATCHLIST_SYMBOLS?.map((stock) => {
              const tick = ticks?.[stock?.symbol];
              const ltp    = tick?.ltp    ?? 0;
              const change = tick?.change ?? 0;
              const pct    = tick?.pct    ?? 0;
              const high   = tick?.high   ?? 0;
              const low    = tick?.low    ?? 0;
              const vol    = tick ? (tick?.volume / 1e6)?.toFixed(1) + 'M' : '—';
              return (
                <tr
                  key={stock?.id}
                  className="border-b border-border/50 hover:bg-muted/40 active:bg-muted/60 transition-colors cursor-pointer group touch-manipulation"
                  onMouseEnter={() => setHovered(stock?.id)}
                  onMouseLeave={() => setHovered(null)}
                >
                  <td className="px-3 md:px-4 py-3">
                    <div>
                      <p className="font-semibold text-foreground">{stock?.symbol}</p>
                      <p className="text-2xs text-muted-foreground truncate max-w-[120px] md:max-w-[140px]">{stock?.name}</p>
                    </div>
                  </td>
                  <td className="px-2 md:px-3 py-3 text-right font-tabular font-semibold text-foreground">
                    ₹{ltp?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`px-2 md:px-3 py-3 text-right font-tabular font-semibold ${change >= 0 ? 'text-positive' : 'text-negative'}`}>
                    <div className="flex items-center justify-end gap-1">
                      {change >= 0 ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      <span>{change >= 0 ? '+' : ''}{pct?.toFixed(2)}%</span>
                    </div>
                  </td>
                  <td className="px-2 md:px-3 py-3 text-right font-tabular text-muted-foreground hidden lg:table-cell">
                    ₹{high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-2 md:px-3 py-3 text-right font-tabular text-muted-foreground hidden lg:table-cell">
                    ₹{low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="px-2 md:px-3 py-3 text-right text-muted-foreground hidden xl:table-cell">{vol}</td>
                  <td className="px-2 md:px-3 py-3 hidden sm:table-cell">
                    <div className={`flex items-center gap-1 transition-opacity ${hovered === stock?.id ? 'opacity-100' : 'opacity-0'}`}>
                      <a href="/trading-screen" className="px-2 py-1 text-2xs font-bold bg-positive/20 text-positive hover:bg-positive/30 rounded transition-all touch-manipulation min-h-[32px] flex items-center">B</a>
                      <a href="/trading-screen" className="px-2 py-1 text-2xs font-bold bg-negative/20 text-negative hover:bg-negative/30 rounded transition-all touch-manipulation min-h-[32px] flex items-center">S</a>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}